import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

export interface MentionAgent {
  _id: string;
  name: string;
}

interface MentionTextareaProps {
  value: string;
  onValueChange: (value: string) => void;
  agents: MentionAgent[];
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  rows?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
}

/** Textarea com autocomplete de "@Nome" — digitar @ abre uma lista dos agentes da organização;
 * escolher um insere "@Nome Completo " no texto. O backend casa esse texto pra decidir quem
 * recebe o e-mail de menção (ver `notifyMentions` em tickets.service.ts). */
export const MentionTextarea = forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  ({ value, onValueChange, agents, placeholder, className, style, disabled, rows, onKeyDown, onPaste }, forwardedRef) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLTextAreaElement);

    const [query, setQuery] = useState<string | null>(null);
    const [atIndex, setAtIndex] = useState(0);
    const [highlight, setHighlight] = useState(0);

    const matches =
      query !== null
        ? agents.filter((a) => a.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
        : [];

    const closeMenu = () => setQuery(null);

    const detectMention = (text: string, cursor: number) => {
      const upToCursor = text.slice(0, cursor);
      const match = /(?:^|\s)@([^\s@]*)$/.exec(upToCursor);
      if (match) {
        setQuery(match[1]);
        setAtIndex(cursor - match[1].length - 1);
        setHighlight(0);
      } else {
        closeMenu();
      }
    };

    const insertMention = (agent: MentionAgent) => {
      if (query === null) return;
      const before = value.slice(0, atIndex);
      const after = value.slice(atIndex + 1 + query.length);
      const next = `${before}@${agent.name} ${after}`;
      onValueChange(next);
      closeMenu();
      requestAnimationFrame(() => {
        const el = innerRef.current;
        if (!el) return;
        const cursorPos = before.length + agent.name.length + 2;
        el.focus();
        el.setSelectionRange(cursorPos, cursorPos);
      });
    };

    return (
      <div className="relative flex-1 min-w-0">
        {matches.length > 0 && (
          <div className="absolute bottom-full left-0 z-20 mb-1 w-56 overflow-hidden rounded-md border border-border bg-popover shadow-md">
            {matches.map((agent, i) => (
              <button
                key={agent._id}
                type="button"
                className={cn(
                  "flex w-full items-center px-3 py-1.5 text-left text-sm",
                  i === highlight ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
                )}
                onMouseEnter={() => setHighlight(i)}
                // onMouseDown (não onClick) — dispara antes do textarea perder o foco/fechar o menu no blur.
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(agent);
                }}
              >
                {agent.name}
              </button>
            ))}
          </div>
        )}
        <Textarea
          ref={innerRef}
          rows={rows}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          className={className}
          style={style}
          onChange={(e) => {
            onValueChange(e.target.value);
            detectMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyDown={(e) => {
            if (matches.length > 0) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlight((h) => (h + 1) % matches.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlight((h) => (h - 1 + matches.length) % matches.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                insertMention(matches[highlight]);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                closeMenu();
                return;
              }
            }
            onKeyDown?.(e);
          }}
          onPaste={onPaste}
          onBlur={closeMenu}
        />
      </div>
    );
  }
);
MentionTextarea.displayName = "MentionTextarea";
